<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, ref } from "vue";
import { Modal, ContactPicker } from "@/components";
import { useStore } from "vuex";
import { isNavigationFailure, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  useCloneDocumentSpaceMutation,
  CrmContact,
} from "@/graphql/operations";
import { blockingDocumentsFromCloneError } from "@/graphql/cloneDocumentSpaceError";
import { useFlashMessage } from "@/hooks";

const store = useStore();
const router = useRouter();
const { t } = useI18n({});
const { displayFlashMessage } = useFlashMessage();

const props = defineProps({
  sourceSpaceId: {
    type: String,
    required: true,
  },
  sourceSpaceName: {
    type: String,
    required: true,
  },
  inheritClassicTheme: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(["close"]);

const withCRMIntegration = computed(() =>
  store.getters.user.hasCRMIntegration(),
);

enum STEPS {
  SELECT_CONTACT = "selectContact",
  INSERT_NAME = "insertName",
}

const currentStep = ref<STEPS>(
  withCRMIntegration.value ? STEPS.SELECT_CONTACT : STEPS.INSERT_NAME,
);

const copyName = () =>
  props.sourceSpaceName
    ? t("documentSpace.copyName", { name: props.sourceSpaceName })
    : "";

const name = ref(copyName());
const isCopying = ref(false);
let mounted = true;
onBeforeUnmount(() => {
  mounted = false;
});
const copyErrorKey = ref<string>();
const blockingDocuments = ref<Array<{ id: string; name: string }>>([]);
const createdSpaceId = ref<string>();
const copyErrorElement = ref<HTMLElement>();

const revealCopyError = async () => {
  await nextTick();
  copyErrorElement.value?.scrollIntoView?.({ block: "nearest" });
  copyErrorElement.value?.focus({ preventScroll: true });
};

const showCopyError = async (key: string) => {
  copyErrorKey.value = key;
  await revealCopyError();
};

const interactionDisabled = computed(
  () => isCopying.value || createdSpaceId.value !== undefined,
);
const canSubmit = computed(
  () => name.value.length > 0 && !interactionDisabled.value,
);
const createdSpaceHref = computed(() =>
  createdSpaceId.value
    ? router.resolve({
        name: "DocumentSpace",
        params: { id: createdSpaceId.value },
      }).href
    : undefined,
);

const selectedContact = ref<CrmContact>();
const setSelectedContact = (contact: CrmContact, defaultName: string) => {
  if (interactionDisabled.value) {
    return;
  }

  selectedContact.value = contact;
  name.value = defaultName;
};

const { executeMutation: executeCopy } = useCloneDocumentSpaceMutation();
const submit = async () => {
  if (!canSubmit.value) {
    return;
  }

  isCopying.value = true;
  copyErrorKey.value = undefined;
  blockingDocuments.value = [];

  try {
    let result;
    try {
      result = await executeCopy({
        id: props.sourceSpaceId,
        newName: name.value,
        crmContactId: selectedContact.value?.id,
      });
    } catch {
      if (mounted) await showCopyError("copySpaceModal.mutationError");
      return;
    }

    // The caller can leave through browser history even while closing is gated.
    if (!mounted) return;

    if (result.error) {
      const blockers = blockingDocumentsFromCloneError(result.error);
      if (blockers) {
        blockingDocuments.value = blockers;
        await showCopyError("copySpaceModal.nonShareableDocumentsError");
      } else {
        await showCopyError("copySpaceModal.mutationError");
      }
      return;
    }

    const clonedSpaceId = result.data?.cloneDocumentSpace?.clonedSpace?.id;
    if (!clonedSpaceId) {
      await showCopyError("copySpaceModal.missingIdError");
      return;
    }

    createdSpaceId.value = clonedSpaceId;

    try {
      const navigationFailure = await router.push({
        name: "DocumentSpace",
        params: { id: clonedSpaceId },
      });

      if (isNavigationFailure(navigationFailure)) {
        await showCopyError("copySpaceModal.navigationError");
        return;
      }
    } catch {
      await showCopyError("copySpaceModal.navigationError");
      return;
    }

    displayFlashMessage(t("copySpaceModal.success"), "success");
    emits("close");
  } finally {
    isCopying.value = false;
  }
};

const skip = async () => {
  if (interactionDisabled.value) {
    return;
  }

  selectedContact.value = undefined;
  name.value = copyName();
  currentStep.value = STEPS.INSERT_NAME;

  if (copyErrorKey.value) {
    await revealCopyError();
  }
};

const goBack = async () => {
  if (interactionDisabled.value) {
    return;
  }

  currentStep.value = STEPS.SELECT_CONTACT;

  if (copyErrorKey.value) {
    await revealCopyError();
  }
};
</script>

<template>
  <Modal
    v-if="currentStep === STEPS.INSERT_NAME"
    :active="true"
    :close-disabled="interactionDisabled"
    :aria-busy="isCopying"
    :scrolling="true"
    :shrink-to-content="true"
    @close="emits('close')"
  >
    <template #header>
      {{ $t("copySpaceModal.header") }}
    </template>
    <template v-slot:body>
      <div>
        <label class="label">
          {{ $t("copySpaceModal.name") }}
        </label>
        <form @submit.prevent="submit">
          <input
            class="input"
            type="text"
            v-model="name"
            ref="inputName"
            :disabled="interactionDisabled"
          />
        </form>
        <div
          v-if="copyErrorKey"
          ref="copyErrorElement"
          class="copy_error"
          role="alert"
          tabindex="-1"
          data-test="copy-error"
        >
          {{ $t(copyErrorKey) }}
          <ul v-if="blockingDocuments.length" data-test="blocking-documents">
            <li
              v-for="document in blockingDocuments"
              :key="document.id"
              class="blocking_document_name"
            >
              {{ document.name }}
            </li>
          </ul>
          <a
            v-if="createdSpaceHref"
            :href="createdSpaceHref"
            data-test="open-created-space"
          >
            {{ $t("copySpaceModal.openCopy") }}
          </a>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="buttons">
        <button
          v-if="withCRMIntegration"
          class="button secondary"
          :disabled="interactionDisabled"
          @click="goBack"
        >
          {{ $t("copySpaceModal.previousStep") }}
        </button>
        <button
          v-else
          @click="emits('close')"
          class="button secondary"
          :disabled="interactionDisabled"
        >
          {{ $t("cancel") }}
        </button>
        <button
          :class="{
            button: true,
            primary: true,
            disabled: !canSubmit,
          }"
          :disabled="!canSubmit"
          @click="submit"
        >
          <span class="material-symbols-outlined">save</span>
          {{
            $t(isCopying ? "copySpaceModal.copying" : "copySpaceModal.submit")
          }}
        </button>
      </div>
    </template>
  </Modal>

  <Modal
    v-else
    :active="true"
    :close-disabled="interactionDisabled"
    :aria-busy="isCopying"
    @close="emits('close')"
    class="medium_large"
    :scrolling="true"
  >
    <template #header>
      {{ $t("copySpaceModal.header") }}
    </template>
    <template v-slot:body>
      <div
        :class="{ interaction_disabled: interactionDisabled }"
        :inert="interactionDisabled"
      >
        <contact-picker
          :inherit-classic-theme="props.inheritClassicTheme"
          @change="setSelectedContact"
          @submit="submit"
        />
      </div>
      <div
        v-if="copyErrorKey"
        ref="copyErrorElement"
        class="copy_error"
        role="alert"
        tabindex="-1"
        data-test="copy-error"
      >
        {{ $t(copyErrorKey) }}
        <ul v-if="blockingDocuments.length" data-test="blocking-documents">
          <li
            v-for="document in blockingDocuments"
            :key="document.id"
            class="blocking_document_name"
          >
            {{ document.name }}
          </li>
        </ul>
        <a
          v-if="createdSpaceHref"
          :href="createdSpaceHref"
          data-test="open-created-space"
        >
          {{ $t("copySpaceModal.openCopy") }}
        </a>
      </div>
    </template>

    <template v-slot:footer>
      <div class="buttons">
        <button
          class="button secondary"
          :disabled="interactionDisabled"
          @click="skip"
        >
          <span class="material-symbols-outlined">save</span>
          {{ $t("copySpaceModal.skip") }}
        </button>
        <button
          :class="{
            button: true,
            primary: true,
            disabled: !selectedContact || !canSubmit,
          }"
          :disabled="!selectedContact || !canSubmit"
          @click="submit"
        >
          <span class="material-symbols-outlined">save</span>
          {{
            $t(isCopying ? "copySpaceModal.copying" : "copySpaceModal.submit")
          }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.interaction_disabled {
  opacity: 0.6;
  pointer-events: none;
}

.copy_error {
  box-sizing: border-box;
  margin-top: 1rem;
  padding: 0.75rem;
  color: var(--classic-error-text, #9f3a38);
  background-color: var(--classic-error-surface, #fff6f6);
  border: 1px solid var(--classic-danger, #e0b4b4);
  border-radius: 3px;
}

.copy_error a {
  display: inline-block;
  margin-left: 0.5rem;
  font-weight: 600;
}

.copy_error ul {
  margin: 0.5rem 0 0;
  padding-left: 1.25rem;
}

.blocking_document_name {
  overflow-wrap: anywhere;
}
</style>
